import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_RETRY_PARAM = "__match_chunk_reload";
const CHUNK_SCOPE_PARAM = "__match_chunk_scope";
const CHUNK_RETRY_STORAGE_PREFIX = "match:lazy-chunk-retry";

type LazyModule<T extends ComponentType<unknown>> = Promise<{ default: T }>;

function getRetryStorageKey(scope: string) {
  return `${CHUNK_RETRY_STORAGE_PREFIX}:${scope}`;
}

export function isDynamicImportFailure(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? "");

  return [
    "Failed to fetch dynamically imported module",
    "Importing a module script failed",
    "error loading dynamically imported module",
  ].some((needle) => message.includes(needle));
}

export function buildChunkRecoveryHref(currentHref: string, scope: string, nonce = Date.now().toString()) {
  const url = new URL(currentHref);
  url.searchParams.set(CHUNK_RETRY_PARAM, nonce);
  url.searchParams.set(CHUNK_SCOPE_PARAM, scope);
  return url.toString();
}

export function clearChunkRecoverySearchParams(urlValue: string) {
  const url = new URL(urlValue);
  url.searchParams.delete(CHUNK_RETRY_PARAM);
  url.searchParams.delete(CHUNK_SCOPE_PARAM);
  return url.toString();
}

export function cleanupChunkRecoveryUrl(currentHref: string) {
  if (typeof window === "undefined") return;

  const currentUrl = new URL(currentHref);
  if (!currentUrl.searchParams.has(CHUNK_RETRY_PARAM) && !currentUrl.searchParams.has(CHUNK_SCOPE_PARAM)) {
    return;
  }

  const sanitizedUrl = clearChunkRecoverySearchParams(currentUrl.toString());
  window.history.replaceState(window.history.state, "", sanitizedUrl);
}

async function resolveLazyModule<T extends ComponentType<unknown>>(
  loader: () => LazyModule<T>,
  scope: string,
) {
  try {
    const module = await loader();

    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(getRetryStorageKey(scope));
    }

    return module;
  } catch (error) {
    if (typeof window === "undefined" || !isDynamicImportFailure(error)) {
      throw error;
    }

    const retryStorageKey = getRetryStorageKey(scope);
    const alreadyRetried = window.sessionStorage.getItem(retryStorageKey) === "1";

    if (!alreadyRetried) {
      window.sessionStorage.setItem(retryStorageKey, "1");
      window.location.replace(buildChunkRecoveryHref(window.location.href, scope));
      return new Promise<never>(() => undefined);
    }

    window.sessionStorage.removeItem(retryStorageKey);
    throw error;
  }
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  loader: () => LazyModule<T>,
  scope: string,
): LazyExoticComponent<T> {
  return lazy(() => resolveLazyModule(loader, scope));
}
